import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Gamepad2, Trophy, Lock, Loader2, Worm, Grid3x3, Car, Crosshair, Candy, Eye } from "lucide-react";
import { api, gamesApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCustomerProfile, useDeviceId } from "@/hooks/useLocal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Wheel, { wheelSegmentCenterDeg } from "@/components/games/Wheel";
import { cn } from "@/lib/utils";

export default function Games() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, signIn } = useAuth();
  const { profile } = useCustomerProfile();
  const deviceId = useDeviceId();
  const phone = user?.phone || profile?.phone || "";
  const [spinOpen, setSpinOpen] = useState(false);
  const [spotOpen, setSpotOpen] = useState(false);

  const configQuery = useQuery({ queryKey: ["gamesConfig"], queryFn: gamesApi.getConfig });
  const stateQuery = useQuery({ queryKey: ["gamesState", "phone", phone], queryFn: () => gamesApi.getState({ phone }), enabled: !!phone });
  const deviceStateQuery = useQuery({ queryKey: ["gamesState", "device", deviceId], queryFn: () => gamesApi.getState({ device_id: deviceId }), enabled: !!deviceId });
  const snakeState = useQuery({ queryKey: ["snakeState"], queryFn: gamesApi.snakeState, enabled: !!user?.phone });
  const blockState = useQuery({ queryKey: ["blockState"], queryFn: gamesApi.blockState, enabled: !!user?.phone });
  const raceState = useQuery({ queryKey: ["raceState"], queryFn: gamesApi.raceState, enabled: !!user?.phone });
  const duckState = useQuery({ queryKey: ["duckState"], queryFn: gamesApi.duckState, enabled: !!user?.phone });
  const candyState = useQuery({ queryKey: ["candyState"], queryFn: gamesApi.candyState, enabled: !!user?.phone });
  const cfg = configQuery.data;

  const arcadeStatus = (data) => {
    if (!user) return "Sign in to unlock";
    if (!user.phone) return "Add phone to play";
    if (!data) return "Loading…";
    if (data.unlocked_today) return "Unlocked — unlimited plays today";
    if (data.free_plays_left > 0) return `${data.free_plays_left} of ${data.free_plays_daily_limit} free plays left`;
    return `Unlock for ${data.unlock_cost} pts`;
  };

  const spotState = (phone ? stateQuery.data?.spot : deviceStateQuery.data?.spot);
  const spotAvailable = spotState?.available ?? true;

  const GAMES = [
    { key: "spot", title: "Spot the Difference", desc: "Find 3 changes, win points", icon: Eye, enabled: cfg?.spot?.enabled, onOpen: () => setSpotOpen(true), status: spotAvailable ? "Available now" : "Come back tomorrow" },
    { key: "spin", title: "Spin the Wheel", desc: "Win up to 200 pts daily", icon: Trophy, enabled: cfg?.spin.enabled, onOpen: () => (user ? setSpinOpen(true) : signIn()), status: user ? (stateQuery.data?.spin.unlocked_today ? "Unlocked today" : `${stateQuery.data?.spin.free_plays_left ?? cfg?.spin.free_plays_daily_limit ?? 5} free spins left`) : "Sign in to spin" },
    { key: "snake", title: "Snake Dash", desc: "Classic snake for points", icon: Worm, enabled: cfg?.snake.enabled, onOpen: () => navigate("/games/snake"), status: arcadeStatus(snakeState.data) },
    { key: "block", title: "Block Puzzle", desc: "Clear lines, earn points", icon: Grid3x3, enabled: cfg?.block.enabled, onOpen: () => navigate("/games/block"), status: arcadeStatus(blockState.data) },
    { key: "race", title: "2D Car Race", desc: "Dodge traffic for points", icon: Car, enabled: cfg?.race.enabled, onOpen: () => navigate("/games/race"), status: arcadeStatus(raceState.data) },
    { key: "duck", title: "Duck Hunt", desc: "Shoot ducks, chain combos", icon: Crosshair, enabled: cfg?.duck?.enabled, onOpen: () => navigate("/games/duck"), status: arcadeStatus(duckState.data) },
    { key: "candy", title: "Candy Match", desc: "Match 3+, pop & cascade", icon: Candy, enabled: cfg?.candy?.enabled, onOpen: () => navigate("/games/candy"), status: arcadeStatus(candyState.data) },
  ].filter((g) => g.enabled !== false);

  return (
    <div className="mx-auto max-w-3xl space-y-5 py-4">
      <div>
        <h1 className="flex items-center gap-2 font-display text-2xl font-bold"><Gamepad2 className="h-6 w-6 text-primary" /> Play & Earn</h1>
        <p className="text-sm text-muted-foreground">Win Goldys points — redeem them for discounts at checkout.</p>
      </div>

      <button onClick={() => navigate("/games/leaderboards")} data-testid="games-leaderboards-link" className="flex w-full items-center justify-between rounded-2xl border border-border bg-card p-4 text-left transition hover:border-primary/40">
        <span className="flex items-center gap-2 font-display text-sm font-bold"><Trophy className="h-5 w-5 text-amber-500" /> Leaderboards</span>
        <span className="text-xs font-semibold text-primary">View all →</span>
      </button>

      {phone ? (
        <button onClick={() => navigate("/wallet")} data-testid="games-wallet-link" className="flex w-full items-center justify-between rounded-2xl bg-primary p-5 text-left text-primary-foreground transition hover:opacity-95">
          <div>
            <p className="text-xs opacity-80">Your game points</p>
            <p className="font-display text-3xl font-bold">{stateQuery.data?.points ?? 0} pts</p>
          </div>
          <span className="rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold">View wallet →</span>
        </button>
      ) : (
        <button onClick={signIn} className="w-full rounded-2xl bg-secondary p-5 text-left">
          <p className="font-display text-base font-bold">Sign in to earn points</p>
          <p className="text-sm text-muted-foreground">Play games and redeem rewards on your orders.</p>
        </button>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {GAMES.map((g) => (          <button key={g.key} onClick={g.onOpen} data-testid={`game-${g.key}`} className="rounded-2xl border border-border bg-card p-5 text-left transition hover:border-primary/40">
            <div className="flex items-center justify-between">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--brand-tertiary))] text-primary"><g.icon className="h-6 w-6" /></div>
              {!user ? <Lock className="h-4 w-4 text-muted-foreground" /> : null}
            </div>
            <p className="mt-3 font-display text-base font-bold">{g.title}</p>
            <p className="text-xs text-muted-foreground">{g.desc}</p>
            <p className="mt-2 text-xs font-medium text-primary">{g.status}</p>
          </button>
        ))}
      </div>

      <SpinDialog open={spinOpen} onClose={() => setSpinOpen(false)} phone={phone} queryClient={queryClient} />
      <SpotDialog open={spotOpen} onClose={() => setSpotOpen(false)} phone={phone} deviceId={deviceId} queryClient={queryClient} signIn={signIn} signedIn={!!user} />
    </div>
  );
}

function SpinDialog({ open, onClose, phone, queryClient }) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const spinningRef = useRef(false);

  const configQuery = useQuery({ queryKey: ["gamesConfig"], queryFn: gamesApi.getConfig, enabled: open });
  const stateQuery = useQuery({ queryKey: ["gamesState", "phone", phone], queryFn: () => gamesApi.getState({ phone }), enabled: open && !!phone });
  const segments = configQuery.data?.spin.segments ?? [];
  const freePlaysLeft = stateQuery.data?.spin.free_plays_left ?? 0;
  const unlockedToday = stateQuery.data?.spin.unlocked_today ?? false;
  const unlockCost = configQuery.data?.spin.unlock_cost ?? 5;
  const points = stateQuery.data?.points ?? 0;
  const canPlay = unlockedToday || freePlaysLeft > 0;
  const canUnlock = !unlockedToday && freePlaysLeft <= 0 && points >= unlockCost;

  const spin = useMutation({
    mutationFn: () => gamesApi.spin(),
    onSuccess: (res) => {
      const targetDeg = wheelSegmentCenterDeg(segments, res.segment_index);
      setRotation((cur) => {
        const current = ((cur % 360) + 360) % 360;
        const deltaToTarget = (((360 - targetDeg - current) % 360) + 360) % 360;
        return cur + 6 * 360 + deltaToTarget;
      });
      setTimeout(() => {
        setSpinning(false);
        spinningRef.current = false;
        setResult({ won: res.points_won > 0, label: res.label });
        queryClient.invalidateQueries({ queryKey: ["gamesState"] });
        queryClient.invalidateQueries({ queryKey: ["loyalty"] });
      }, 3300);
    },
    onError: (e) => {
      setSpinning(false);
      spinningRef.current = false;
      toast.error(e.message || "Could not spin right now");
      queryClient.invalidateQueries({ queryKey: ["gamesState"] });
    },
  });

  const unlock = useMutation({
    mutationFn: gamesApi.spinUnlock,
    onSuccess: () => {
      toast.success("Unlocked — spin as much as you like today!");
      queryClient.invalidateQueries({ queryKey: ["gamesState"] });
    },
    onError: (e) => toast.error(e.message || "Could not unlock"),
  });

  const handleSpin = () => {
    if (spinningRef.current) return;
    setResult(null);
    setSpinning(true);
    spinningRef.current = true;
    spin.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !spinningRef.current && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Spin the Wheel</DialogTitle></DialogHeader>
        <div className="flex flex-col items-center gap-4">
          <p className="text-sm text-muted-foreground">Balance: <span className="font-bold text-foreground">{points} pts</span></p>
          {segments.length ? <Wheel segments={segments} rotation={rotation} spinning={spinning} /> : <Loader2 className="h-8 w-8 animate-spin text-primary" />}
          {result ? <p data-testid="spin-result" className={cn("rounded-full px-4 py-2 text-sm font-semibold", result.won ? "bg-[hsl(var(--brand-tertiary))] text-primary" : "bg-secondary text-muted-foreground")}>{result.won ? `${result.label} 🎉` : "Better luck next time!"}</p> : null}
          <p className="text-center text-xs text-muted-foreground">{unlockedToday ? "Unlimited spins unlocked today" : `${freePlaysLeft} free spins left today`}</p>
          {canPlay ? (
            <button onClick={handleSpin} disabled={spinning} data-testid="spin-button" className="w-full rounded-full bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50">{spinning ? "Spinning…" : "Spin"}</button>
          ) : (
            <button onClick={() => unlock.mutate()} disabled={!canUnlock || unlock.isPending} data-testid="spin-unlock-button" className="w-full rounded-full bg-primary py-3 font-bold text-primary-foreground disabled:opacity-50">
              {canUnlock ? `Unlock unlimited for ${unlockCost} pts` : "Not enough points — come back tomorrow"}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SpotDialog({ open, onClose, phone, deviceId, queryClient, signIn, signedIn }) {
  // Web port of the mobile Spot the Difference game: memorise the original, then tap the 3 changes on
  // the modified image before the timer runs out. Taps are normalised 0..1 against the shown image so
  // they match the backend's stored target coordinates.
  const [phase, setPhase] = useState("starting"); // starting | reveal | play | submitting | result | error
  const [session, setSession] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [taps, setTaps] = useState([]);
  const [outcome, setOutcome] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const submittedRef = useRef(false);
  const imgRef = useRef(null);

  const start = useMutation({
    mutationFn: () => gamesApi.spotStart({ phone, device_id: deviceId }),
    onSuccess: (res) => {
      setSession(res);
      setTaps([]);
      setOutcome(null);
      submittedRef.current = false;
      setPhase("reveal");
      setCountdown(res.reveal_seconds);
    },
    onError: (e) => { setErrorMsg(e.message || "Could not start the game"); setPhase("error"); },
  });

  const submit = useMutation({
    mutationFn: (finalTaps) => gamesApi.spotSubmit({ session_id: session.session_id, phone, device_id: deviceId, taps: finalTaps }),
    onSuccess: (res) => {
      setOutcome(res);
      setPhase("result");
      queryClient.invalidateQueries({ queryKey: ["gamesState"] });
      queryClient.invalidateQueries({ queryKey: ["loyalty"] });
    },
    onError: (e) => { setErrorMsg(e.message || "Could not submit your attempt"); setPhase("error"); },
  });

  const doSubmit = (finalTaps) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    setPhase("submitting");
    submit.mutate(finalTaps);
  };

  useEffect(() => {
    if (open) { setPhase("starting"); setErrorMsg(""); start.mutate(); }
  }, [open]);

  // Reveal → play
  useEffect(() => {
    if (phase !== "reveal") return;
    if (countdown <= 0) { setPhase("play"); setCountdown(session?.time_limit_seconds ?? 15); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown, session]);

  // Play timer → auto-submit
  useEffect(() => {
    if (phase !== "play") return;
    if (countdown <= 0) { doSubmit(taps); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown]);

  const onImageClick = (e) => {
    if (phase !== "play" || taps.length >= 3) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    const next = [...taps, { x, y }];
    setTaps(next);
    if (next.length >= 3) doSubmit(next);
  };

  const imgBase = session ? `/games/spot/${session.image_id}` : "";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Spot the Difference</DialogTitle></DialogHeader>
        {phase === "starting" || start.isPending ? (
          <div className="flex justify-center py-10"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
        ) : phase === "error" ? (
          <div className="rounded-xl bg-secondary p-5 text-center text-sm text-muted-foreground">{errorMsg}</div>
        ) : session ? (
          <div className="flex flex-col items-center gap-3">
            <div className="relative w-full overflow-hidden rounded-xl border border-border" style={{ aspectRatio: "2 / 1" }}>
              {phase === "reveal" ? (
                <img src={`${imgBase}-original.png`} alt="Memorise this" className="h-full w-full object-cover" />
              ) : (
                <>
                  <img
                    ref={imgRef}
                    src={`${imgBase}-modified.png`}
                    alt="Find the differences"
                    data-testid="spot-image"
                    onClick={onImageClick}
                    className={cn("h-full w-full object-cover", phase === "play" ? "cursor-crosshair" : "")}
                    draggable={false}
                  />
                  {taps.map((t, i) => (
                    <span key={i} className="pointer-events-none absolute h-7 w-7 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-primary" style={{ left: `${t.x * 100}%`, top: `${t.y * 100}%` }} />
                  ))}
                </>
              )}
            </div>

            {phase === "reveal" ? (
              <p className="text-sm font-semibold text-foreground">Memorise it! Hiding in {countdown}s…</p>
            ) : phase === "play" ? (
              <p className="text-sm font-semibold text-foreground" data-testid="spot-hint">Tap the {3 - taps.length} change{3 - taps.length === 1 ? "" : "s"} you spot — {countdown}s left</p>
            ) : phase === "submitting" ? (
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            ) : null}

            {phase === "result" && outcome ? (
              <div data-testid="spot-result" className={cn("w-full rounded-xl p-4 text-center", outcome.success ? "bg-[hsl(var(--brand-tertiary))]" : "bg-secondary")}>
                {outcome.success ? (
                  <>
                    <p className="font-display text-lg font-bold text-primary">You found them all! 🎉</p>
                    {phone ? (
                      <p className="text-sm text-muted-foreground">+{outcome.points_awarded} points added to your balance.</p>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">{outcome.pending_points} points waiting — sign in to claim them.</p>
                        {!signedIn ? <button onClick={signIn} data-testid="spot-sign-in-claim" className="mt-2 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground">Sign in with Google</button> : null}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-display text-lg font-bold">So close!</p>
                    <p className="text-sm text-muted-foreground">{outcome.matched}/3 found. Come back tomorrow for another try.</p>
                  </>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
